import {
  isPreviewNotReadyError,
  useAlertContext,
  useConfirmationDialogContext,
  useConvertToPdfConfirmation,
} from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { DocumentGroup, assetInventoryDocumentsApi, caseNotesApi, documentsApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import {
  fetchClientDocumentBlob,
  fetchClientDocumentPreviewBlob,
} from "@/common/components/layout/components/dropzone/hooks/dropzoneFetch.utils";
import { downloadFile } from "@/common/helper/fileDownloadHelper";
import { getLocalTimeZoneId } from "@/common/utils/getLocalTimeZoneId";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

interface UseAssetInventoryDocumentActionsParams {
  clientId: string;
  source: DocumentGroup;
  listQueryKey: unknown[];
}

export interface AssetInventoryDocumentActions {
  rename: (documentId: string, newFileName: string, fileName: string) => void;
  download: (documentId: string, fileName: string) => void;
  createCaseNote: (documentId: string, fileName: string) => void;
  convertToPdf: (documentId: string, fileName: string) => void;
  fetchPreviewBlob: (documentId: string) => Promise<Blob>;
  requestDelete: (documentId: string, fileName: string) => void;
  markRead: (documentId: string, isRead: boolean) => void;
  isBusy: boolean;
}

/**
 * Document actions shared by the asset-inventory document lists (bank statements and
 * dependant documents), mirroring the DropZone behavior: rename, download, preview,
 * create case note from document, confirm-then-convert to PDF, and confirm-then-delete.
 */
export function useAssetInventoryDocumentActions({
  clientId,
  source,
  listQueryKey,
}: UseAssetInventoryDocumentActionsParams): AssetInventoryDocumentActions {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useAlertContext();
  const { showYesNoDialog } = useConfirmationDialogContext();
  const confirmConvertToPdf = useConvertToPdfConfirmation();

  const invalidateList = () => queryClient.invalidateQueries({ queryKey: listQueryKey });

  const renameMutation = useMutation({
    mutationFn: ({ documentId, newFileName }: { documentId: string; newFileName: string; fileName: string }) =>
      documentsApi.renameDocument({ clientId, documentId, renameDocumentRequest: { newFileName } }),
    onSuccess: (_data, { newFileName }) => {
      showSuccess(t("dropzone.fileList.renameSuccess", { fileName: newFileName }));
      void invalidateList();
    },
    onError: (_error, { fileName }) => showError(t("dropzone.fileList.renameError", { fileName })),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ documentId }: { documentId: string; fileName: string }) =>
      documentsApi.deleteDocuments({ clientId, ids: [documentId] }),
    onSuccess: (_data, { fileName }) => {
      showSuccess(t("dropzone.fileList.deleteSuccess", { fileName }));
      void invalidateList();
    },
    onError: (_error, { fileName }) => showError(t("dropzone.fileList.deleteError", { fileName })),
  });

  const createCaseNoteMutation = useMutation({
    mutationFn: ({ documentId }: { documentId: string; fileName: string }) =>
      caseNotesApi.createCaseNoteFromDocument({
        clientId,
        createCaseNoteFromDocumentRequest: { source, fileId: documentId, originTimeZoneId: getLocalTimeZoneId() },
      }),
    onSuccess: (_data, { fileName }) => {
      showSuccess(t("dropzone.fileList.createCaseNoteSuccess", { fileName }));
      void invalidateList();
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientCaseNotes, clientId] });
    },
    onError: (_error, { fileName }) => showError(t("dropzone.fileList.createCaseNoteError", { fileName })),
  });

  const convertToPdfMutation = useMutation({
    mutationFn: ({ documentId }: { documentId: string; fileName: string }) =>
      documentsApi.convertDocumentToPdf({ clientId, documentId }, withLocalErrorHandling),
    onSuccess: (_data, { fileName }) => {
      showSuccess(t("dropzone.fileList.convertToPdfSuccess", { fileName }));
      void invalidateList();
    },
    onError: (error, { fileName }) =>
      showError(
        isPreviewNotReadyError(error)
          ? t("dropzone.fileList.convertToPdfNotReady", { fileName })
          : t("dropzone.fileList.convertToPdfError", { fileName })
      ),
  });

  const markReadMutation = useMutation({
    mutationFn: ({ documentId, isRead }: { documentId: string; isRead: boolean }) =>
      assetInventoryDocumentsApi.markAssetInventoryDocumentRead({
        clientId,
        documentId,
        markAssetInventoryDocumentReadRequest: { isRead },
      }),
    onSuccess: () => void invalidateList(),
  });

  const download = (documentId: string, fileName: string) => {
    fetchClientDocumentBlob(clientId, documentId)
      .then((blob) => downloadFile(blob, fileName))
      .catch(() => showError(t("dropzone.fileList.downloadError")));
  };

  const requestDelete = (documentId: string, fileName: string) => {
    const text = `${t("dropzone.fileList.bulkDeleteConfirmText")}\n\n• ${fileName}`;
    showYesNoDialog(t("dropzone.fileList.bulkDeleteConfirmTitle"), text, (confirmed) => {
      if (confirmed) {
        deleteMutation.mutate({ documentId, fileName });
      }
    });
  };

  return {
    rename: (documentId: string, newFileName: string, fileName: string) =>
      renameMutation.mutate({ documentId, newFileName, fileName }),
    download,
    createCaseNote: (documentId: string, fileName: string) => createCaseNoteMutation.mutate({ documentId, fileName }),
    convertToPdf: (documentId: string, fileName: string) =>
      confirmConvertToPdf(fileName, () => convertToPdfMutation.mutate({ documentId, fileName })),
    fetchPreviewBlob: (documentId: string) => fetchClientDocumentPreviewBlob(clientId, documentId),
    requestDelete,
    markRead: (documentId: string, isRead: boolean) => markReadMutation.mutate({ documentId, isRead }),
    isBusy:
      renameMutation.isPending ||
      deleteMutation.isPending ||
      createCaseNoteMutation.isPending ||
      markReadMutation.isPending,
  };
}
